import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import Hls from 'hls.js';
import { frameTransform } from '../../utils/mediaFrame';

/**
 * The media layer of a story frame. Uploaded clips stream over HLS (hls.js, or natively on
 * Safari/iOS) exactly like the event gallery does, so there's one player in the product.
 *
 * `object-fit: contain` on black is deliberate: a 16:9 or square clip is letterboxed, never
 * cropped. People shoot what they shoot.
 */
const StoryMedia = forwardRef(function StoryMedia({
  item,
  active = true,
  muted = true,
  paused = false,
  onEnded = null,
  onError = null,
  onDuration = null,
  /** (currentTime, duration) on every timeupdate — the story video player draws its clock from it. */
  onTime = null,
  /** (videoWidth, videoHeight) once the clip's size is known — the portrait rule reads it. */
  onDimensions = null,
  /** px of the frame's bottom under the slab; the picture centres in what's left. */
  cover = 0,
  /** 'contain' letterboxes (stories); 'cover' fills the frame (a Portal's card, composer v2). */
  fit = 'contain',
  /** Start over instead of ending — the share sheet's capture needs the clip to keep going. */
  loop = false,
  /** A trimmed clip loops inside its window by default; the story video player turns this off
   *  and ends the item itself when the window runs out. */
  windowLoop = true,
}, ref) {
  const videoRef = useRef(null);
  const isVideo = item?.kind === 'video' && !!item?.mediaUrl;
  // One retry per clip, without CORS. See `onMediaError`.
  const retried = useRef(false);
  useEffect(() => {
    retried.current = false;
    // A new clip asks for CORS again — the drop below is for the clip that failed, not for ever.
    // Declared before the effect that sets `src`, so it runs first and the attribute is on the
    // element before the load starts, which is the only order WebKit accepts.
    const v = videoRef.current;
    if (v && !v.crossOrigin) v.crossOrigin = 'anonymous';
  }, [item?.mediaUrl]);

  /**
   * A clip that refuses to load gets one more go without `crossorigin` before anybody is told
   * it failed. WebKit plays HLS itself, and when the attribute is on the element it holds every
   * manifest, rendition and segment to the CORS check — one hop that doesn't satisfy it (a
   * redirect, a cached response without the header) and the whole clip errors. Dropping it
   * costs the share card its live frame, which falls back to black; keeping it cost iPhones
   * every video Portal.
   */
  const onMediaError = useCallback(() => {
    const v = videoRef.current;
    const stream = !!v && !/^(blob|data):/.test(item?.mediaUrl || '');
    if (v && stream && v.crossOrigin && !retried.current) {
      retried.current = true;
      v.removeAttribute('crossorigin');
      v.src = item.mediaUrl;
      try { v.load(); } catch { /* it is loading already */ }
      return;
    }
    onError?.();
  }, [item?.mediaUrl, onError]);

  useImperativeHandle(ref, () => ({
    restart: () => { const v = videoRef.current; if (v) { try { v.currentTime = 0; } catch { /* not seekable yet */ } } },
    /** Seek to a second of the file. The player clamps; this just lands it. */
    seek: (t) => { const v = videoRef.current; if (v) { try { v.currentTime = t; } catch { /* not seekable yet */ } } },
    currentTime: () => videoRef.current?.currentTime || 0,
    element: () => videoRef.current,
  }), []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return undefined;
    let hls;
    // A local file (your own clip, still going up) plays as itself; only streams need HLS.
    if (/^(blob|data):/.test(item.mediaUrl)) {
      v.src = item.mediaUrl;
    } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
      v.src = item.mediaUrl;
    } else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(item.mediaUrl);
      hls.attachMedia(v);
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data?.fatal) onError?.(); });
    } else {
      onError?.();
    }
    return () => { if (hls) hls.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideo, item?.mediaUrl]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return;
    v.muted = muted;
  }, [muted, isVideo]);

  // The Portal is the slice the sender kept: play inside it and loop there, rather than
  // running on into footage nobody chose. The file itself is still the whole thing.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return undefined;
    const startMs = item?.trimStartMs;
    const endMs = item?.trimEndMs;
    if (!windowLoop || !(endMs > startMs)) return undefined;
    const start = startMs / 1000;
    const end = endMs / 1000;
    const onTick = () => {
      if (v.currentTime >= end - 0.06 || v.currentTime < start - 0.1) {
        try { v.currentTime = start; } catch { /* not seekable yet */ }
      }
    };
    v.addEventListener('timeupdate', onTick);
    return () => v.removeEventListener('timeupdate', onTick);
  }, [isVideo, item?.trimStartMs, item?.trimEndMs, windowLoop]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return;
    if (!active || paused) { v.pause?.(); return; }
    // Opens muted — every mobile browser refuses sound it wasn't asked for, and a story that
    // silently fails to start is worse than one that starts quiet.
    // play() returns a promise everywhere current; wrapped so an old engine's undefined can't throw.
    Promise.resolve(v.play?.()).catch(() => { v.muted = true; Promise.resolve(v.play?.()).catch(() => onError?.()); });
  }, [active, paused, isVideo, onError]);

  if (!item) return null;
  // Inert to touch: a long press on a photo or clip must never bring up the browser's own
  // save / share sheet. Gestures belong to the frame around it.
  const placed = { objectFit: fit, background: '#000', transform: frameTransform(item.frame, cover), transformOrigin: 'center', pointerEvents: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none', WebkitUserDrag: 'none' };

  if (!isVideo) {
    return (
      <img
        data-story-media
        src={item.mediaUrl || item.thumbnailUrl}
        alt=""
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        className="absolute inset-0 h-full w-full"
        style={placed}
        onError={() => onError?.()}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      // Android Chrome and Safari play the HLS stream natively, straight off Cloudflare, and a
      // cross-origin clip drawn onto the share canvas taints it: the Instagram loop then died
      // with "canvas is not origin-clean". Stream answers every manifest, segment and thumbnail
      // with ACAO *, so asking for CORS costs nothing and keeps the pixels readable. Set here,
      // before the src lands in the effect — it has to be on the element first. hls.js (MSE)
      // and a local blob: never needed it and are unaffected.
      crossOrigin="anonymous"
      data-testid="story-clip"
      playsInline
      muted={muted}
      disablePictureInPicture
      controlsList="nodownload noremoteplayback"
      onContextMenu={(e) => e.preventDefault()}
      poster={item.thumbnailUrl || undefined}
      className="absolute inset-0 h-full w-full"
      style={placed}
      onEnded={(e) => {
        if (loop) {
          const v = e.currentTarget;
          try { v.currentTime = (item?.trimStartMs || 0) / 1000; } catch { /* not seekable yet */ }
          Promise.resolve(v.play?.()).catch(() => {});
          return;
        }
        onEnded?.();
      }}
      onError={onMediaError}
      onLoadedMetadata={(e) => { onDuration?.(e.currentTarget.duration); if (e.currentTarget.videoWidth) onDimensions?.(e.currentTarget.videoWidth, e.currentTarget.videoHeight); }}
      onTimeUpdate={onTime ? (e) => onTime(e.currentTarget.currentTime, e.currentTarget.duration) : undefined}
    />
  );
});

export default StoryMedia;
